import {
  Box,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  ExtremumTypeEnum,
  MasterFieldTypeEnum,
  ResponseNumberSubTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { FormikProps } from 'formik'
import type { FC } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { SubTypeText } from 'src/constants/responseNumber'
import type { RootState } from 'src/state/store'
import type { NumberRuleProps } from './EditResponseNumberRuleEnableProductMasterDrawer'

export const MasterRuleSelector: FC<{
  formikProps: FormikProps<NumberRuleProps>
}> = ({
  formikProps: {
    values: { subType, scale, minimumRuleVariableDef, maximumRuleVariableDef },
    handleChange,
    setFieldValue,
  },
}) => {
  const update = (key: keyof NumberRuleProps) => (value: unknown) =>
    setFieldValue(key, value)

  const productMasterFieldDefs = useSelector(
    (state: RootState) =>
      state.productMasterFieldDefsState.productMasterFieldDefs.filter(
        productMasterFieldDef =>
          productMasterFieldDef.fieldType === MasterFieldTypeEnum.Number,
      ),
    shallowEqual,
  )

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Stack direction="row" spacing={0.5} alignItems="center">
        {subType !== ResponseNumberSubTypeEnum.LESS_THAN && (
          <Select<number | ''>
            displayEmpty
            size="small"
            value={minimumRuleVariableDef?.masterFieldDefId ?? ''}
            onChange={e => {
              const value = e.target.value
              if (value === '') return
              setFieldValue('minimumRuleVariableDef', {
                masterFieldDefId: value,
                extremumType: ExtremumTypeEnum.Min,
              })
            }}
            inputProps={{ 'aria-label': '最小値の選択' }}
            renderValue={value =>
              value === '' ? (
                <Box component="span" sx={{ color: 'text.disabled' }}>
                  最小値を選択
                </Box>
              ) : (
                (productMasterFieldDefs.find(f => f.id === value)?.name ?? '')
              )
            }
            sx={{ width: 184 }}
          >
            {productMasterFieldDefs.map(productMasterFieldDef => (
              <MenuItem
                key={productMasterFieldDef.id}
                value={productMasterFieldDef.id}
                data-testid="select-minimum-master-field-def-item"
              >
                {productMasterFieldDef.name}
              </MenuItem>
            ))}
          </Select>
        )}
        {(!subType || subType === ResponseNumberSubTypeEnum.BETWEEN) && (
          <Typography>ー</Typography>
        )}
        {subType !== ResponseNumberSubTypeEnum.GREATER_THAN && (
          <Select<number | ''>
            displayEmpty
            size="small"
            value={maximumRuleVariableDef?.masterFieldDefId ?? ''}
            onChange={e => {
              const value = e.target.value
              if (value === '') return
              setFieldValue('maximumRuleVariableDef', {
                masterFieldDefId: value,
                extremumType: ExtremumTypeEnum.Max,
              })
            }}
            inputProps={{ 'aria-label': '最大値の選択' }}
            renderValue={value =>
              value === '' ? (
                <Box component="span" sx={{ color: 'text.disabled' }}>
                  最大値を選択
                </Box>
              ) : (
                (productMasterFieldDefs.find(f => f.id === value)?.name ?? '')
              )
            }
            sx={{ width: 184 }}
          >
            {productMasterFieldDefs.map(productMasterFieldDef => (
              <MenuItem
                key={productMasterFieldDef.id}
                value={productMasterFieldDef.id}
                data-testid="select-maximum-master-field-def-item"
              >
                {productMasterFieldDef.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TextField
          size="small"
          name="scale"
          placeholder="℃"
          value={scale ?? ''}
          onChange={handleChange}
          slotProps={{ htmlInput: { 'aria-label': '正常値の単位' } }}
          sx={{ width: 80 }}
        />
        <Select<ResponseNumberSubTypeEnum | ''>
          displayEmpty
          size="small"
          value={subType ?? ''}
          onChange={e => update('subType')(e.target.value || undefined)}
          inputProps={{ 'aria-label': '正常値の範囲指定' }}
          renderValue={value =>
            value === '' ? (
              <Box component="span" sx={{ color: 'text.disabled' }}>
                の間
              </Box>
            ) : (
              SubTypeText[value]
            )
          }
          sx={{ width: 80 }}
        >
          <MenuItem
            value={ResponseNumberSubTypeEnum.BETWEEN}
            data-testid="select-number-subtype-item"
          >
            {SubTypeText[ResponseNumberSubTypeEnum.BETWEEN]}
          </MenuItem>
          <MenuItem
            value={ResponseNumberSubTypeEnum.GREATER_THAN}
            data-testid="select-number-subtype-item"
          >
            {SubTypeText[ResponseNumberSubTypeEnum.GREATER_THAN]}
          </MenuItem>
          <MenuItem
            value={ResponseNumberSubTypeEnum.LESS_THAN}
            data-testid="select-number-subtype-item"
          >
            {SubTypeText[ResponseNumberSubTypeEnum.LESS_THAN]}
          </MenuItem>
        </Select>
      </Stack>
    </Stack>
  )
}
